import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

import Button from '@/components/Buttons/Button';
import { PageInformationContext } from '@/context/PageInformationContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import type { MarketApps } from '@/features/Dashboard/types';
import { useMarketPreview } from '@/features/Markets/api';
import MarketsComponents from '@/pages/Markets/MarketsComponents';
import MarketsSkeleton from '@/pages/Markets/MarketsSkeleton';

import { DashboardActionButtons } from '../../components/DashboardActionButtons';
import { DashboardLayout } from '../../components/DashboardLayout';
import { DashboardShareDialog } from '../../components/DashboardShareDialog';
import type { DashboardStep } from '../../constants/DashboardStep';
import { DASHBOARD_ACTION_BUTTON_WIDTH } from '../../constants/style';

export const ViewDashboard = ({
  setStep,
  dashboardConfig,
  tabNames,
  activeTab,
  title,
  onTabChange
}: {
  setStep: Dispatch<SetStateAction<DashboardStep>>;
  dashboardConfig: MarketApps | undefined;
  tabNames: string[];
  activeTab: number;
  title: string;
  onTabChange: (index: number) => void;
}) => {
  const { data, isLoading, isFetching } = useMarketPreview(dashboardConfig);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('create');
  }, [setMode]);

  // Show loading for content area when fetching (keeps tabs visible for optimistic switching)
  const showContentLoading = isLoading || isFetching;

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={
          <DashboardActionButtons setStep={setStep}>
            <Button
              color='secondary'
              size='large'
              startIcon={<ShareOutlinedIcon />}
              sx={{
                width: DASHBOARD_ACTION_BUTTON_WIDTH
              }}
              variant='contained'
              onClick={() => {
                setShareDialogOpen(true);
              }}
            >
              Share
            </Button>
          </DashboardActionButtons>
        }
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        {showContentLoading ? (
          <MarketsSkeleton />
        ) : (
          data && (
            <MarketsComponents
              components={data.components}
              showAddToDashboard={false}
              showShareBlock={false}
            />
          )
        )}
      </DashboardLayout>
      <DashboardShareDialog
        open={shareDialogOpen}
        title={title}
        onClose={() => {
          setShareDialogOpen(false);
        }}
      />
    </PageInformationContext>
  );
};
